/** Creates the workspace-scoped view models (once per signed-in workspace) and mounts the app shell. */
import { ActivityViewModel, BookingPagesViewModel, CalendarViewModel, MeetSwapViewModel, ContactsViewModel, MailNotifyViewModel, MailboxViewModel, MailSettingsViewModel, DndViewModel, DriveViewModel, EmojiUsageViewModel, HuddleViewModel, HuddlesViewModel, LaterViewModel, ScheduledViewModel, PageTreeViewModel, Shell, ShellViewModel, SidebarViewModel, StickerCreatorViewModel, StickersViewModel, TemplatesViewModel, WorkspaceViewModel, workspaceCallHost, type WorkspaceRef } from "@bundle/core";
import { AccountLinkingContext, BillingContext, useLocale, DriveContext, HuddleContext, LiveMediaContext, SettingsContext, SharingContext, StickerSheets, StickersContext, TemplatesContext, useViewModel, WorkspaceContext, type HuddleContextValue, type SettingsContextValue, type StickersContextValue } from "@bundle/ui";
import { useCallback, useEffect, useMemo } from "react";
import type { WarmEntry } from "./warmWorkspaces.ts";
import { systemClock, ulidIds } from "../platform/clock.ts";
import type { Container } from "../platform/container.ts";
import { webEmojiCatalog } from "../platform/emoji.ts";
import { APP_VERSION, WEB_ORIGIN } from "../platform/env.ts";
import { featureFlags } from "../platform/features.ts";
import { huddlePlatform } from "../platform/huddle/index.ts";
import { networkMonitor } from "../platform/network.ts";
import { stickersPlatform } from "../platform/stickers/index.ts";
import { templatesApi } from "../platform/templates/index.ts";
import { driveApi } from "../platform/drive/index.ts";
import { isDesktop } from "../platform/desktop.ts";
import { desktopSync, throttled } from "../platform/desktopSync.ts";
import { TemplatesHost } from "./TemplatesHost.tsx";
import { AppModalHost } from "./AppModalHost.tsx";
import { sharingPlatform } from "../platform/sharing/index.ts";
import { billingPlatform } from "../platform/billing/index.ts";
import { settingsPlatform } from "../platform/settings/index.ts";
import { accountSecurityFor } from "../platform/accountSecurity.ts";
import { moveWorkspaceSlug } from "../platform/settings/slugChange.ts";
import { linkingContext } from "../platform/linking.ts";
import { notificationSound } from "../platform/notificationSound.ts";
import { initialLocale } from "../platform/preferences.tsx";
import { format, type Locale, type MessageKey } from "@bundle/i18n";
import { AppShell } from "./AppShell.tsx";
import { IncomingHuddle } from "./IncomingHuddle.tsx";
import { RingAlerts } from "./RingAlerts.tsx";
import { emitMailEvent, registerCalendar, registerMail, type MailServices } from "@bundle/ui";
import { mailAttachmentOpener, uploadMailAttachment } from "../platform/mailFiles.ts";
import { addActivitySource } from "../platform/activity.ts";
import { MailComposerHost } from "../screens/mail/MailComposer.tsx";
import { registerEmojiUsage, registerLater, registerScheduled } from "@bundle/ui";

type Vms = {
  workspace: WorkspaceViewModel;
  sidebar: SidebarViewModel;
  shell: ShellViewModel;
  huddle: HuddleViewModel | undefined;
  /** The Huddles area (with the call view model). */
  huddles: HuddlesViewModel | undefined;
  /** Files. */
  drive: DriveViewModel;
  /** Stickers & GIFs (flag `stickers`): the library and the shared creator. */
  stickers: StickersContextValue | undefined;
  /** Templates (flag `templates`): gallery, saved templates, Duplicate, row templates. */
  templates: TemplatesViewModel | undefined;
  /** Activity feed + badge and Do not disturb (flag `settings`: they use the settings API). */
  activity: ActivityViewModel | undefined;
  dnd: DndViewModel | undefined;
  /** Mail, Contacts, Calendar and Settings → Mail (online only). */
  mail: MailServices;
};

/** Panes that fit the window: desktop 4, tablet 2, phone 1. */
export function maxPanesFor(width: number): number {
  if (width >= 1280) return 4;
  if (width >= 820) return 2;
  return 1;
}

/** Kept warm across switches (warmWorkspaces.ts): only the workspace in front is live. */
type OwnedVms = Vms & WarmEntry;

/** Saves a text file through a download (Settings → two-step backup codes). */
function saveTextFile(name: string, text: string): void {
  const url = URL.createObjectURL(new Blob([text], { type: "text/plain;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function WorkspaceRoot({ container, userId, workspace }: { container: Container; userId: string; workspace: WorkspaceRef }) {
  const slug = workspace.slug;
  const wsId = workspace.id;
  const create = useCallback((): OwnedVms => {
    // Only this person's mail stays on the device, for the workspaces they're in (the others open at once too).
    container.snapshots.keepWorkspaces(userId, [wsId, ...(container.session.state.workspaces ?? []).map((w) => w.id)]);
    // The huddle view model is made after the workspace one, which hands it huddle rings (onNotification).
    const huddleRef: { vm?: HuddleViewModel } = {};
    const laterRef: { vm?: LaterViewModel } = {};
    const driveRef: { vm?: DriveViewModel } = {};
    const scheduledRef: { vm?: ScheduledViewModel } = {};
    const mailRef: { vm?: MailServices; started?: boolean } = {};
    // Activity and Do not disturb use the settings API (flag `settings`).
    // Desktop Mirror sync: a remote Files change tells the sync engine to poll now (files-desktop-sync.md).
    const nudgeSync = isDesktop() ? throttled(() => void desktopSync(false).nudge().catch(() => undefined), 2000) : undefined;
    const settingsApi = featureFlags.settings ? settingsPlatform(container).api : undefined;
    const activity = settingsApi === undefined ? undefined : new ActivityViewModel({ api: settingsApi, clock: systemClock }, slug, userId);
    const dnd = settingsApi === undefined ? undefined : new DndViewModel({ api: settingsApi, clock: systemClock, timeZone: () => Intl.DateTimeFormat().resolvedOptions().timeZone });
    const vm = new WorkspaceViewModel(
      {
        api: container.api,
        data: container.data,
        realtime: container.realtime,
        clock: systemClock,
        ids: ulidIds,
        storage: container.storage,
        snapshots: container.snapshots,
        network: networkMonitor,
        platform: "web",
        clientVersion: APP_VERSION,
        // Names the client translates (the Support bot), in the current language (Mounted re-applies on a change).
        translate: (d) => format(viewerLocale.current ?? initialLocale(container.storage), d.key as MessageKey, d.params as never),
        docs: container.docs,
        // Composer autocomplete ships behind a flag (platform/features.ts): off, no menu opens and @ just types "@".
        autocomplete: featureFlags.autocomplete,
        emoji: featureFlags.autocomplete ? webEmojiCatalog : undefined,
        // DMs and mentions as desktop/web notifications (platform/notifier.ts decides whether to show them).
        onNotification: (event) => {
          container.notifier.notify(event);
          activity?.onNotification(event);
          huddleRef.vm?.onNotification(event);
          laterRef.vm?.onNotification(event);
        },
        onHuddleSignal: (signal) => huddleRef.vm?.onSignal(signal),
        onEvent: (event) => {
          activity?.onEvent(event);
          scheduledRef.vm?.onEvent(event);
          driveRef.vm?.onEvent(event);
          if (event.type === "drive.changed") nudgeSync?.();
          const mail = mailRef.vm;
          if (mail !== undefined && (event.type.startsWith("mail.") || event.type.startsWith("calendar."))) {
            mail.mailbox.onEvent(event);
            mail.calendar.onEvent(event);
            mail.mailSettings.onEvent(event);
            emitMailEvent(event);
          }
        },
      },
      wsId,
      userId,
    );
    const sidebar = new SidebarViewModel(vm, { folders: featureFlags.channels });
    // Files: loads when its sidebar or screen first shows; drive.changed refetches what is open.
    const drive = new DriveViewModel(vm, driveApi(container));
    driveRef.vm = drive;
    // Huddles ship behind a flag (platform/features.ts): without it there is no call view model and no huddle UI.
    const calls = featureFlags.huddles ? huddlePlatform(container) : undefined;
    const huddle = calls === undefined ? undefined : new HuddleViewModel(workspaceCallHost(vm), { api: calls.api, engine: calls.engine });
    if (huddle !== undefined) huddleRef.vm = huddle;
    // The Huddles area (live, upcoming, recent, my links): loads when its sidebar or screen first shows.
    const huddles = huddle === undefined || calls === undefined ? undefined : new HuddlesViewModel(vm, huddle, calls.api, !calls.browserOnly);
    // Stickers and GIFs ship behind a flag (platform/features.ts): without it composers keep the plain emoji picker.
    const stickerVm = featureFlags.stickers ? new StickersViewModel(vm, stickersPlatform(container)) : undefined;
    const stickers = stickerVm === undefined ? undefined : { stickers: stickerVm, creator: new StickerCreatorViewModel(stickerVm) };
    void stickerVm?.refresh();
    // Templates ship behind a flag (platform/features.ts): without it the sidebar "+" and databases look as before.
    const templateTree = featureFlags.templates ? new PageTreeViewModel(vm) : undefined;
    const templates = templateTree === undefined ? undefined : new TemplatesViewModel(vm, templateTree, templatesApi(container));
    // Split groups and tab groups ship behind a flag (platform/features.ts): off, a split is window-wide, as before.
    const shell = new ShellViewModel(container.storage, ulidIds, slug, `/${slug}`, maxPanesFor(window.innerWidth), { tabs: featureFlags.tabs, owner: userId });
    container.data.failOrphanedSends(wsId);
    // Notifications and the app-icon badge follow the workspace in front (attached on resume).
    let detachNotifier: (() => void) | undefined;
    // Automatic updates wait while I'm in a call, sending or uploading (platform/updates.ts, core Updates.safeToRestart).
    const detachActivity = addActivitySource(`ws:${wsId}`, () => ({
      inCall: huddle !== undefined && huddle.state.call.status !== "idle",
      uploads: container.uploads.inFlight(),
      pendingWrites: container.queryClient.isMutating(),
    }));
    void activity?.refresh();
    void dnd?.load();
    // Later: saved messages + reminders (the screen, the sidebar badge and the message menus share it).
    const later = new LaterViewModel(vm);
    laterRef.vm = later;
    const unregisterLater = registerLater(vm, later);
    void later.refresh();
    // Scheduled messages (the composer bar, the send menu and the Scheduled list share it).
    const scheduled = new ScheduledViewModel(vm);
    scheduledRef.vm = scheduled;
    const unregisterScheduled = registerScheduled(vm, scheduled);
    void scheduled.refresh();
    // "Frequently used" emoji (pickers + the hover bar's quick reactions).
    const emojiUsage = new EmojiUsageViewModel(vm);
    const unregisterEmojiUsage = registerEmojiUsage(vm, emojiUsage);
    void emojiUsage.refresh();
    // Mail + Calendar: the mailbox loads now (the sidebar's unread badge); the others when their screen opens.
    const mail: MailServices = {
      mailbox: new MailboxViewModel(vm),
      contacts: new ContactsViewModel(vm),
      calendar: new CalendarViewModel(vm, Intl.DateTimeFormat().resolvedOptions().timeZone, 0),
      meetSwap: new MeetSwapViewModel(vm),
      mailSettings: new MailSettingsViewModel(vm),
      notify: new MailNotifyViewModel(vm),
      ...mailAttachmentOpener(container.api, wsId),
      uploadAttachment: (file) => uploadMailAttachment(container.api, wsId, file),
    };
    mailRef.vm = mail;
    // Calendar is its own area (no Mail needed): its events and my booking pages load when the area first shows.
    const booking = new BookingPagesViewModel(vm);
    const unregisterCalendar = registerCalendar(vm, { calendar: mail.calendar, booking, meetSwap: mail.meetSwap });
    // Behind the server's MAIL_WORKSPACES flag (bootstrap `mail`): registered only once the workspace has it, so the
    // sidebar, routes and Settings → Mail stay hidden otherwise and nothing calls the (404) mail routes.
    let unregisterMail = () => {};
    const startMail = () => {
      if (vm.state.cache?.mail !== true || mailRef.started) return;
      mailRef.started = true;
      unregisterMail = registerMail(vm, mail);
      void mail.mailbox.revalidate();
    };
    const unsubscribeMailFlag = vm.subscribe(startMail);
    startMail();
    // The first resume starts it: what was just made is already loading.
    let resumed = false;
    return {
      mail,
      workspace: vm,
      sidebar,
      shell,
      huddle,
      huddles,
      drive,
      stickers,
      templates,
      activity,
      dnd,
      resume: () => {
        vm.resume();
        detachNotifier ??= container.notifier.attach({ meId: userId, shell, workspace: vm, paused: () => dnd?.state.paused === true, rings: huddle !== undefined });
        if (!resumed) {
          resumed = true;
          return;
        }
        // Back from another workspace: the socket replays chat; catch up on what doesn't come over it.
        if (mailRef.started === true) void mail.mailbox.revalidate();
        activity?.refreshSoon();
        void dnd?.load();
        void later.refresh();
        void scheduled.refresh();
      },
      suspend: () => {
        // Leaving the workspace leaves its huddle, as before the switch kept it warm (its UI goes with the shell).
        if (huddle !== undefined && huddle.state.call.status !== "idle") void huddle.leave();
        detachNotifier?.();
        detachNotifier = undefined;
        vm.suspend();
      },
      dispose: () => {
        unsubscribeMailFlag();
        unregisterMail();
        unregisterCalendar();
        booking.dispose();
        mail.mailbox.dispose();
        mail.contacts.dispose();
        mail.calendar.dispose();
        mail.meetSwap.dispose();
        mail.mailSettings.dispose();
        mail.notify.dispose();
        unregisterLater();
        later.dispose();
        unregisterScheduled();
        scheduled.dispose();
        unregisterEmojiUsage();
        emojiUsage.dispose();
        activity?.dispose();
        dnd?.dispose();
        templates?.dispose();
        templateTree?.dispose();
        detachNotifier?.();
        detachActivity();
        stickers?.creator.dispose();
        stickers?.stickers.dispose();
        huddles?.dispose();
        drive.dispose();
        huddle?.dispose();
        sidebar.dispose();
        vm.dispose();
        shell.dispose();
      },
    };
  }, [container, wsId, userId, slug]);
  // Kept warm across switches, like Slack: switching back paints from these at once, then catches up.
  const key = `${userId}:${wsId}`;
  const vms = useMemo(() => container.warm.obtain(key, create) as OwnedVms, [container, key, create]);
  useEffect(() => {
    container.warm.activate(key);
    return () => container.warm.deactivate(key);
  }, [container, key]);

  useEffect(() => {
    const onResize = () => vms.shell.setMaxPanes(maxPanesFor(window.innerWidth));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [vms]);

  return <Mounted vms={vms} container={container} slug={slug} />;
}

/** The viewer's language for view-model translations (WorkspaceDeps.translate). */
const viewerLocale: { current?: Locale } = {};

function Mounted({ vms, container, slug }: { vms: Vms; container: Container; slug: string }) {
  const ws = useViewModel(vms.workspace);
  const locale = useLocale();
  useEffect(() => {
    if (viewerLocale.current === locale) return;
    viewerLocale.current = locale;
    vms.workspace.relocalize();
  }, [locale, vms]);
  const value = useMemo(() => ({ workspace: vms.workspace, sidebar: vms.sidebar, slug }), [vms, slug]);

  useEffect(() => {
    if (!ws.authRejected) return;
    void container.session.authRejected().then((renewed) => {
      if (renewed) vms.workspace.reauthenticated();
    });
  }, [ws.authRejected, container.session, vms]);

  const w = ws.cache?.workspace;
  useEffect(() => {
    if (w === undefined) return;
    // A new URL slug: keep the open tabs (and the page) on the new address before the app switches to it.
    if (w.slug !== slug) moveWorkspaceSlug(container.storage, slug, w.slug);
    container.session.rememberWorkspace({ id: w.id, slug: w.slug, name: w.name });
  }, [w, slug, container.session, container.storage]);

  // Settings → Account → "Sign-in methods" (account linking), wherever settings are on.
  const linking = useMemo(() => (featureFlags.settings ? linkingContext(container) : null), [container]);
  // Calls: the workspace's huddle view model, the engine's live streams, and "open in browser" on Linux desktop.
  const calls = vms.huddle === undefined ? undefined : huddlePlatform(container);
  const huddle = useMemo<HuddleContextValue | null>(
    () =>
      vms.huddle === undefined || calls === undefined
        ? null
        : {
            vm: vms.huddle,
            browserOnly: calls.browserOnly,
            openInBrowser: (channelId) => calls.openInBrowser(Shell.formatUrl({ type: "huddle", ws: slug, huddleId: channelId })),
            webOrigin: WEB_ORIGIN,
            ...(vms.huddles === undefined ? {} : { area: vms.huddles }),
          },
    [vms, calls, slug],
  );
  // Page/file sharing ships behind a flag (platform/features.ts): without the API in context there is no sharing UI.
  const sharingApi = useMemo(() => (featureFlags.sharing ? sharingPlatform(container) : null), [container]);
  // Billing lives in Settings (Settings → Billing, banners, limit prompts): same flag as the settings screens.
  const billing = useMemo(() => (featureFlags.settings ? billingPlatform(container) : null), [container]);
  // Settings ship behind a flag (platform/features.ts): without it there are no settings screens or member cards.
  const settings = useMemo<SettingsContextValue | null>(
    () =>
      featureFlags.settings
        ? {
            deps: settingsPlatform(container),
            notifications: container.notifications,
            sound: notificationSound,
            copy: (text) => navigator.clipboard.writeText(text),
            origin: location.origin,
            locale: () => initialLocale(container.storage),
            activity: vms.activity,
            dnd: vms.dnd,
            accountSecurity: accountSecurityFor(container),
            saveTextFile,
          }
        : null,
    [container, vms],
  );
  // Back in the foreground: catch up on Activity and Do not disturb changed on another device.
  useEffect(() => {
    const onFocus = () => {
      vms.activity?.refreshSoon();
      void vms.dnd?.load();
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [vms]);
  // Leave the huddle when the page goes away (the request uses keepalive); heartbeats time out otherwise.
  useEffect(() => {
    const onHide = (e: PageTransitionEvent) => {
      if (!e.persisted) void vms.huddle?.leave();
    };
    window.addEventListener("pagehide", onHide);
    return () => window.removeEventListener("pagehide", onHide);
  }, [vms]);

  return (
    <WorkspaceContext.Provider value={value}>
      <AccountLinkingContext.Provider value={linking}>
      <SettingsContext.Provider value={settings}>
      <SharingContext.Provider value={sharingApi}>
      <BillingContext.Provider value={billing}>
      <StickersContext.Provider value={vms.stickers ?? null}>
      <TemplatesContext.Provider value={vms.templates ?? null}>
      <DriveContext.Provider value={vms.drive}>
        <HuddleContext.Provider value={huddle}>
          {calls === undefined ? (
            <AppShell shell={vms.shell} container={container} />
          ) : (
            <LiveMediaContext.Provider value={calls.streams}>
              <AppShell shell={vms.shell} container={container} />
              <IncomingHuddle shell={vms.shell} slug={slug} />
              <RingAlerts shell={vms.shell} slug={slug} paused={() => vms.dnd?.state.paused === true} />
            </LiveMediaContext.Provider>
          )}
        </HuddleContext.Provider>
        {vms.stickers === undefined ? null : <StickerSheets testID="stickers" />}
        <MailComposerHost />
        {vms.templates === undefined ? null : <TemplatesHost vm={vms.templates} shell={vms.shell} slug={slug} />}
        <AppModalHost ws={vms.workspace} />
      </DriveContext.Provider>
      </TemplatesContext.Provider>
      </StickersContext.Provider>
      </BillingContext.Provider>
      </SharingContext.Provider>
      </SettingsContext.Provider>
      </AccountLinkingContext.Provider>
    </WorkspaceContext.Provider>
  );
}
